import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    UserCheck, CreditCard, ClipboardCheck, Sparkles, ShieldCheck,
    Edit3, Wallet, Rocket, ArrowRight, Check,
} from "lucide-react";
import MarketingShell from "@/components/MarketingShell";

/** /how-it-works — Scroll-triggered animated narrative.
 *
 *  Design notes:
 *    - Uses vanilla IntersectionObserver — same pattern the SSR doctor
 *      pages use. No `motion` / framer-motion dep needed.
 *    - Respects `prefers-reduced-motion`: reveals resolve to opacity:1
 *      instantly (no translate/scale animation) so people using vestibular
 *      or cognitive accessibility settings aren't punished.
 *    - Left rail is a sticky progress spine that fills as the reader
 *      scrolls through each step. Fills using the same IO callback.
 *    - 8 steps mirror the copy on the `how-it-works` CMS page — kept
 *      here as source of truth for animation, deep-linkable by anchor.
 */
const STEPS = [
    { n: 1, icon: UserCheck,      title: "Sign up & verify",
      body: "Create your account with email, mobile and medical registration number. Verify OTP in seconds.",
      tint: "#2563EB" },
    { n: 2, icon: CreditCard,     title: "Choose a package",
      body: "Starter, Professional, Premium, Elite or Legacy Platinum — all listed transparently on the Pricing page.",
      tint: "#0F766E" },
    { n: 3, icon: Wallet,         title: "Pay 50% advance",
      body: "Cashfree, RBI-authorised. GST-compliant invoice generated on successful payment. Webhook-verified — no client-only confirmations.",
      tint: "#B8862B" },
    { n: 4, icon: ClipboardCheck, title: "Complete onboarding",
      body: "A structured 8-step wizard: identity, education, experience, clinics, credentials, achievements. Roughly 30 focused minutes.",
      tint: "#2563EB" },
    { n: 5, icon: Sparkles,       title: "AI-refined profile",
      body: "Biography, page copy, SEO meta and FAQs — composed from YOUR facts. Missing sections are flagged \"Information Required\". Never fabricated.",
      tint: "#0F766E" },
    { n: 6, icon: ShieldCheck,    title: "Admin review",
      body: "One working day. Light verification of credentials. Any issue is returned to you with clear reasons — no black-box rejections.",
      tint: "#B8862B" },
    { n: 7, icon: Edit3,          title: "You approve",
      body: "Read every AI-generated section. Regenerate anything, edit inline, then approve. Nothing publishes without your explicit sign-off.",
      tint: "#2563EB" },
    { n: 8, icon: Rocket,         title: "Pay balance & publish",
      body: "Pay the balance 50% — your profile goes live at /dr/your-name with a QR visiting card. Share, embed, indexed on Google.",
      tint: "#0F766E" },
];

const HIGHLIGHTS = [
    "Zero fabrication — AI grounded on your facts, always.",
    "Dual approval — admin AND you, before anything publishes.",
    "50/50 payment — advance to start, balance to publish.",
    "Server-verified webhooks — no client-only payment confirmations.",
    "Public URL you own — /dr/your-name and QR card included.",
];

function useReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const on = () => setReduced(!!mq.matches);
        on();
        mq.addEventListener?.("change", on);
        return () => mq.removeEventListener?.("change", on);
    }, []);
    return reduced;
}

export default function HowItWorks() {
    const [progress, setProgress] = useState(0);
    const [active, setActive] = useState(0);
    const reduced = useReducedMotion();
    const stepRefs = useRef([]);
    stepRefs.current = STEPS.map((_, i) => stepRefs.current[i] || null);

    // Scroll-reveal: mark step as revealed when it enters the viewport,
    // and track which step is closest to the mid-line for the sticky rail.
    useEffect(() => {
        const revealIo = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) e.target.classList.add("is-revealed");
            });
        }, { rootMargin: "0px 0px -12% 0px", threshold: 0.15 });
        const active_io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    const idx = parseInt(e.target.dataset.stepIndex, 10);
                    if (!Number.isNaN(idx)) {
                        setActive(idx);
                        setProgress(((idx + 1) / STEPS.length) * 100);
                    }
                }
            });
        }, { rootMargin: "-40% 0px -40% 0px", threshold: 0 });
        stepRefs.current.forEach((el) => {
            if (!el) return;
            revealIo.observe(el);
            active_io.observe(el);
        });
        return () => { revealIo.disconnect(); active_io.disconnect(); };
    }, []);

    // Meta.
    useEffect(() => {
        document.title = "How It Works · Doctor Digital Identity Platform";
        const m = document.querySelector('meta[name="description"]');
        if (m) m.setAttribute("content", "The doctor's journey — signup, package, onboarding, AI profile, admin review, publish. Eight clear steps with dual approval.");
    }, []);

    // HowTo JSON-LD — helps Google surface the step-by-step guide in
    // search results (rich result: "8 steps: ...").
    useEffect(() => {
        const id = "howitworks-jsonld";
        document.getElementById(id)?.remove();
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        el.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "Publish a Doctor Digital Identity page",
            description: "The 8-step journey a doctor follows to publish a verified professional identity on Doctor Digital Identity Platform.",
            totalTime: "PT45M",
            step: STEPS.map((s) => ({
                "@type": "HowToStep",
                position: s.n,
                name: s.title,
                text: s.body,
                url: (typeof window !== "undefined" ? window.location.origin : "") + `/how-it-works#step-${s.n}`,
            })),
        });
        document.head.appendChild(el);
        return () => document.getElementById(id)?.remove();
    }, []);

    const revealCls = reduced
        ? ""
        : "opacity-0 translate-y-6 transition-all duration-700 ease-out";

    return (
        <MarketingShell>
            <style>{`
                .step-revealed.is-revealed,
                .step-revealed[data-reduced="1"] { opacity: 1 !important; transform: translateY(0) !important; }
                .step-revealed .step-icon { transition: transform .6s cubic-bezier(.16,1,.3,1); }
                .step-revealed.is-revealed .step-icon { transform: rotate(-4deg) scale(1.05); }
            `}</style>

            <section className="max-w-7xl mx-auto px-6 pt-16 pb-10 text-center" data-testid="howitworks-page">
                <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold" data-testid="howitworks-eyebrow">
                    The journey · 8 steps
                </div>
                <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#123B5D] tracking-tight leading-[1.05]" data-testid="howitworks-title">
                    From signup to a published<br className="hidden sm:block" /> professional identity.
                </h1>
                <p className="mt-5 text-lg text-[#17212B]/70 max-w-2xl mx-auto">
                    Every step is auditable. Every AI-composed line is grounded on facts you provided.
                    Nothing publishes without your explicit approval.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to="/signup" className="inline-flex items-center gap-2 bg-[#0F766E] hover:bg-[#0d655e] text-white px-6 py-2.5 rounded-full text-sm font-semibold transition" data-testid="howitworks-cta-signup">
                        Start now <ArrowRight size={14} />
                    </Link>
                    <Link to="/pricing" className="inline-flex items-center gap-2 text-[#123B5D] font-semibold hover:underline text-sm" data-testid="howitworks-cta-pricing">
                        See packages first
                    </Link>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                {/* Sticky progress spine (desktop only). */}
                <aside className="hidden lg:block lg:col-span-3 relative">
                    <div className="sticky top-28" data-testid="howitworks-spine">
                        <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 font-semibold mb-3">
                            Your progress
                        </div>
                        <div className="text-4xl font-extrabold text-[#123B5D] tabular-nums">
                            <span data-testid="howitworks-progress-value">{Math.round(progress)}</span>%
                        </div>
                        <div className="mt-4 h-1.5 rounded-full bg-[#123B5D]/10 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#2563EB] via-[#0F766E] to-[#B8862B] transition-all duration-500 ease-out"
                                style={{ width: `${progress}%` }}
                                data-testid="howitworks-progress-bar"
                            />
                        </div>
                        <div className="mt-6 space-y-2 text-sm">
                            {STEPS.map((s, i) => (
                                <div
                                    key={s.n}
                                    className={
                                        "flex items-center gap-2 transition-all duration-300 " +
                                        (i === active ? "text-[#123B5D] font-semibold" : "text-[#17212B]/45")
                                    }
                                    data-testid={`howitworks-spine-step-${s.n}`}
                                >
                                    <span
                                        className={
                                            "h-1.5 w-1.5 rounded-full transition-colors " +
                                            (i <= active ? "bg-[#0F766E]" : "bg-[#123B5D]/20")
                                        }
                                    />
                                    Step {s.n} · {s.title}
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Steps stack. */}
                <div className="lg:col-span-9 space-y-14 lg:space-y-24">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <article
                                key={s.n}
                                ref={(el) => (stepRefs.current[i] = el)}
                                id={`step-${s.n}`}
                                data-step-index={i}
                                data-reduced={reduced ? "1" : "0"}
                                className={"step-revealed relative " + revealCls}
                                data-testid={`howitworks-step-${s.n}`}
                            >
                                <div className="flex items-start gap-5">
                                    <div
                                        className="step-icon shrink-0 h-14 w-14 rounded-2xl flex items-center justify-center text-white shadow-md"
                                        style={{ background: s.tint }}
                                    >
                                        <Icon size={22} strokeWidth={2.1} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: s.tint }}>
                                            Step {s.n} of {STEPS.length}
                                        </div>
                                        <h2 className="mt-1 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#123B5D] leading-tight">
                                            {s.title}
                                        </h2>
                                        <p className="mt-3 text-[15px] sm:text-base text-[#17212B]/75 leading-relaxed max-w-prose">
                                            {s.body}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* Guarantees strip */}
            <section className="bg-[#F5F8FA] border-y border-[#123B5D]/10" data-testid="howitworks-highlights">
                <div className="max-w-6xl mx-auto px-6 py-14">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Guarantees</div>
                    <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#123B5D]">What never bends, regardless of package.</h2>
                    <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {HIGHLIGHTS.map((h) => (
                            <li key={h} className="flex items-start gap-3 bg-white border border-[#123B5D]/10 rounded-lg px-4 py-3">
                                <Check size={16} className="text-[#0F766E] shrink-0 mt-0.5" />
                                <span className="text-[14px] text-[#17212B]/85">{h}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="max-w-4xl mx-auto text-center px-6 py-16">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#123B5D]">
                    Ready to start?
                </h2>
                <p className="mt-3 text-[#17212B]/70">Signup takes under two minutes. Payment happens only after you've picked a package.</p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to="/signup" className="inline-flex items-center gap-2 bg-[#0F766E] hover:bg-[#0d655e] text-white px-6 py-2.5 rounded-full text-sm font-semibold" data-testid="howitworks-final-cta">
                        Create your identity <ArrowRight size={14} />
                    </Link>
                    <Link to="/help" className="text-sm text-[#123B5D] font-semibold hover:underline">
                        Talk to the AI assistant
                    </Link>
                </div>
            </section>
        </MarketingShell>
    );
}
